// ─── Template: stats-grid-v1 ───────────────────────────────────────────────
// Three columns of stat + label that animate in cascading from left to
// right. Numbers count up. Used for "by the numbers" sections.

import React from 'react';
import { AbsoluteFill, Easing, interpolate, useCurrentFrame, useVideoConfig } from 'remotion';
import type { AnimationTemplate } from '../types';

const FONT_STACK = 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

export type StatsGridProps = {
  stat1Value: string;
  stat1Label: string;
  stat2Value: string;
  stat2Label: string;
  stat3Value: string;
  stat3Label: string;
  /** When true, numeric values count up from 0. */
  countUp: boolean;
  numberFont: string;
  numberColor: string;
  labelFont: string;
  labelColor: string;
  background: string;
  /** Color of the dividers between stats. */
  dividerColor: string;
};

const StatColumn: React.FC<{
  value: string; label: string; countUp: boolean;
  delayFrames: number; numberFont: string; numberColor: string;
  labelFont: string; labelColor: string; numberPx: number; labelPx: number;
}> = ({
  value, label, countUp, delayFrames,
  numberFont, numberColor, labelFont, labelColor, numberPx, labelPx,
}) => {
  const frame = useCurrentFrame();
  const local = frame - delayFrames;
  const op = interpolate(local, [0, 18], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const y = interpolate(local, [0, 18], [16, 0], {
    easing: Easing.out(Easing.cubic), extrapolateLeft: 'clamp', extrapolateRight: 'clamp',
  });

  // Number parse + count-up
  const target = parseFloat((value || '').replace(/[,_\s]/g, '').replace(/[^\d.\-]/g, ''));
  const hasNumber = Number.isFinite(target);
  const count = countUp && hasNumber
    ? interpolate(local, [4, 26], [0, target], {
        easing: Easing.out(Easing.cubic), extrapolateLeft: 'clamp', extrapolateRight: 'clamp',
      })
    : target;
  const decimals = (value.split('.')[1] || '').length;
  const displayCore = hasNumber
    ? count.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    : value;
  // Preserve any non-digit characters from the original (e.g. "$", "%").
  const display = hasNumber
    ? value.replace(/[\d.,]+/, displayCore)
    : value;

  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
      opacity: op, transform: `translateY(${y}px)`,
    }}>
      <span style={{
        fontFamily: `${numberFont}, ${FONT_STACK}`,
        fontSize: numberPx,
        color: numberColor,
        fontWeight: 800,
        lineHeight: 1,
        letterSpacing: -2,
        fontVariantNumeric: 'tabular-nums',
      }}>{display}</span>
      <span style={{
        fontFamily: `${labelFont}, ${FONT_STACK}`,
        fontSize: labelPx,
        color: labelColor,
        fontWeight: 500,
        marginTop: numberPx * 0.18,
        textAlign: 'center',
        letterSpacing: 0.5,
      }}>{label}</span>
    </div>
  );
};

const Component: React.FC<StatsGridProps> = ({
  stat1Value, stat1Label, stat2Value, stat2Label, stat3Value, stat3Label,
  countUp, numberFont, numberColor, labelFont, labelColor, background, dividerColor,
}) => {
  const { width, height } = useVideoConfig();
  // Calibrado sobre el lado más corto: en vertical (9:16) los números
  // antes ocupaban toda la pantalla y se cortaban.
  const refDim = Math.min(width, height);
  const numberPx = refDim * 0.13;
  const labelPx = refDim * 0.025;

  const stats = [
    { value: stat1Value, label: stat1Label, delay: 0 },
    { value: stat2Value, label: stat2Label, delay: 8 },
    { value: stat3Value, label: stat3Label, delay: 16 },
  ];

  return (
    <AbsoluteFill style={{
      background, fontFamily: FONT_STACK,
      alignItems: 'center', justifyContent: 'center', flexDirection: 'row',
      padding: '0 8%',
    }}>
      {stats.map((s, i) => (
        <React.Fragment key={i}>
          <StatColumn
            value={s.value} label={s.label} countUp={countUp}
            delayFrames={s.delay}
            numberFont={numberFont} numberColor={numberColor}
            labelFont={labelFont} labelColor={labelColor}
            numberPx={numberPx} labelPx={labelPx}
          />
          {i < stats.length - 1 && (
            <div style={{
              width: 1,
              height: numberPx * 0.9,
              background: dividerColor,
              opacity: 0.4,
              margin: '0 1%',
            }} />
          )}
        </React.Fragment>
      ))}
    </AbsoluteFill>
  );
};

export const statsGridTemplate: AnimationTemplate<StatsGridProps> = {
  id: 'stats-grid-v1',
  label: 'Estadísticas en grilla',
  description: 'Tres números con etiqueta que aparecen en cascada con conteo animado.',
  category: 'text',
  defaultDurationSec: 4,
  defaultProps: {
    stat1Value: '120', stat1Label: 'Clientes',
    stat2Value: '4.8', stat2Label: 'Rating promedio',
    stat3Value: '99%',  stat3Label: 'Satisfacción',
    countUp: true,
    numberFont: 'Inter', numberColor: '#FFFFFF',
    labelFont: 'Inter',  labelColor: '#A3A3A3',
    background: '#0B0F1A',
    dividerColor: '#FFFFFF',
  },
  propsSchema: {
    // Los 6 inputs comparten estilo en grupos: los `*Value` toman
    // `numberFont`/`numberColor`, los `*Label` toman `labelFont`/`labelColor`.
    // Apuntar todos al mismo grupo de styling hace que cualquier de los
    // tres "Dato N — número" abra el mismo popover de tipografía/color
    // (es lo que el usuario espera: editan los números como conjunto).
    stat1Value: { type: 'text', label: 'Dato 1 — número', placeholder: '120', agentExposed: true,
                  styling: { font: 'numberFont', color: 'numberColor' } },
    stat1Label: { type: 'text', label: 'Dato 1 — etiqueta', placeholder: 'Clientes', agentExposed: true,
                  styling: { font: 'labelFont', color: 'labelColor' } },
    stat2Value: { type: 'text', label: 'Dato 2 — número', placeholder: '4.8', agentExposed: true,
                  styling: { font: 'numberFont', color: 'numberColor' } },
    stat2Label: { type: 'text', label: 'Dato 2 — etiqueta', placeholder: 'Rating', agentExposed: true,
                  styling: { font: 'labelFont', color: 'labelColor' } },
    stat3Value: { type: 'text', label: 'Dato 3 — número', placeholder: '99%', agentExposed: true,
                  styling: { font: 'numberFont', color: 'numberColor' } },
    stat3Label: { type: 'text', label: 'Dato 3 — etiqueta', placeholder: 'Satisfacción', agentExposed: true,
                  styling: { font: 'labelFont', color: 'labelColor' } },
    countUp:    { type: 'boolean', label: 'Animar el conteo' },
    // Estos siguen declarados — son los targets del `styling` de arriba.
    // El editor los esconde del top-level porque están "linked"; sólo
    // se editan desde el popover "Aa" de cada input.
    numberFont:   { type: 'font',  label: 'Tipografía de números' },
    numberColor:  { type: 'color', label: 'Color de números' },
    labelFont:    { type: 'font',  label: 'Tipografía de etiquetas' },
    labelColor:   { type: 'color', label: 'Color de etiquetas' },
    background:   { type: 'color', label: 'Fondo', allowTransparent: true },
    dividerColor: { type: 'color', label: 'Color de los divisores' },
  },
  Component,
};
